import React from "react";
import {
  Box,
  Grid,
  Typography,
  Container,
  Card,
  CardContent,
  CardMedia,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";

// Styled Components
const PageSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  position: "relative",
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  position: "relative",
  fontWeight: 700,
  paddingBottom: theme.spacing(2),
  display: "inline-block",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: "80px",
    height: "4px",
    backgroundColor: "#C10E14",
  },
}));

const LetsBuildYourFuture = () => {
  return (
    <PageSection
      sx={{
        bgcolor: "#f8f9fc",
        py: { xs: 8, md: 8 },
        // mt: { xs: 8, md: 10 },
      }}
    >
      <Container>
        <Box sx={{ maxWidth: "1200px", mx: "auto" }}>
          {/* Two-Column Layout */}
          <Grid container spacing={8} alignItems="center">
            {/* Left Column - Content */}
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Box sx={{ textAlign: { xs: "center", md: "left" } }}>
                  <Typography
                    variant="h3"
                    component="h2"
                    fontWeight={700}
                    sx={{
                      mb: 4,
                      fontSize: { xs: "2.2rem", md: "3rem" },
                      color: "#1a1a1a",
                      lineHeight: 1.2,
                    }}
                  >
                    Let's Build Your Future in Dubai
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "18px",
                      color: "#555",
                      lineHeight: 1.7,
                      mb: 2,
                      textAlign: {
                        xs: "justify",
                        sm: "justify",
                        md: "left",
                      },
                      textJustify: "inter-word",
                    }}
                  >
                    At <strong>DESCO Real Estate</strong>, we're more than just
                    agents — we're your trusted partners in Dubai's dynamic
                    property landscape. Whether you're planning to{" "}
                    <strong>buy a luxurious home</strong>,{" "}
                    <strong>sell a high-value investment</strong>, or{" "}
                    <strong>rent a comfortable residence</strong>, our 10+ years
                    of local expertise and client-first approach make your
                    journey smooth, successful, and rewarding.
                  </Typography>
                </Box>
              </motion.div>
            </Grid>

            {/* Right Column - Services */}
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    alignItems: { xs: "center", md: "flex-start" },
                  }}
                >
                  {/* Service 1 */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      textAlign: { xs: "center", md: "left" },
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "center", sm: "flex-start" },
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        backgroundColor: "#ffe6e6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        mt: { xs: 0, sm: 0.5 },
                      }}
                    >
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          // backgroundColor: "#C10E14",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "white",
                            fontSize: "12px",
                            fontWeight: "bold",
                          }}
                        >
                          📞
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        sx={{
                          color: "#1a1a1a",
                          mb: 1,
                          fontSize: "18px",
                        }}
                      >
                        Talk to our expert agents
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#666",
                          fontSize: "16px",
                          lineHeight: 1.5,
                        }}
                      >
                        Get personalized property guidance from real
                        professionals.
                      </Typography>
                    </Box>
                  </Box>

                  {/* Service 2 */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      textAlign: { xs: "center", md: "left" },
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "center", sm: "flex-start" },
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        backgroundColor: "#ffe6e6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        mt: { xs: 0, sm: 0.5 },
                      }}
                    >
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          // backgroundColor: "#C10E14",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "white",
                            fontSize: "12px",
                            fontWeight: "bold",
                          }}
                        >
                          📍
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        sx={{
                          color: "#1a1a1a",
                          mb: 1,
                          fontSize: "18px",
                        }}
                      >
                        Visit our office or book a virtual consultation
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#666",
                          fontSize: "16px",
                          lineHeight: 1.5,
                        }}
                      >
                        Flexible meetings at your convenience — in-person or
                        online.
                      </Typography>
                    </Box>
                  </Box>

                  {/* Service 3 */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      textAlign: { xs: "center", md: "left" },
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "center", sm: "flex-start" },
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        backgroundColor: "#ffe6e6",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        mt: { xs: 0, sm: 0.5 },
                      }}
                    >
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          // backgroundColor: "#C10E14",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "white",
                            fontSize: "12px",
                            fontWeight: "bold",
                          }}
                        >
                          🔍
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        sx={{
                          color: "#1a1a1a",
                          mb: 1,
                          fontSize: "18px",
                        }}
                      >
                        Explore our curated listings
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#666",
                          fontSize: "16px",
                          lineHeight: 1.5,
                        }}
                      >
                        Discover handpicked properties across Dubai's top
                        locations.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </motion.div>
            </Grid>
          </Grid>

          {/* Bottom Message */}
          <Box sx={{ textAlign: "center", mt: 6 }}>
            <Typography
              variant="body1"
              sx={{
                color: "#888",
                fontSize: "16px",
                fontStyle: "italic",
              }}
            >
              Your dream property is just one step away — let's make it real.
            </Typography>
          </Box>
        </Box>
      </Container>
    </PageSection>
  );
};

export default LetsBuildYourFuture;
